import React, {useEffect, useState} from "react";
import { Accordion, AccordionDetails, AccordionSummary, 
  Container, Grid, ListItem, TextField, Typography,
  List, Table, TableBody, TableCell, TableRow, TableContainer,
  TableHead, Button, InputLabel,
  Paper, Select, IconButton,
  MenuItem,
  FormControl,
  Autocomplete,
  Skeleton,
  Stack} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Api from "../services/Api";
import { NewSolutionForm } from "../components/NewSolutionForm";
import { NewStationForm } from "../components/NewStationForm";
import { ReadableDateTime } from "../utils/momentUtils";
import { NewAssignmentForm } from "../components/NewAssignmentForm";
import { DayOfWeekArray } from "../utils/constants";
import { NewMetricForm } from "../components/NewMetricForm";
import { Box } from "@mui/system";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useUserInfoContext } from "../contexts/UserInfoContext";
import { UnauthorizedPage } from "../components/auth/Unauthorized";
import UserUtils from "../utils/userUtils";
import useDocumentTitle from "../hooks/useDocumentTitle";
import UserRoleUpdater from "../components/UserRoleUpdater";


const sectionsList = [

]

const AdminSolutionView = ({solutions, refresh, ...props}) => {

  const createNewSolution = async (solName) => {
    let result = await Api.postNewSolution(solName);
    if (result){
      refresh();
    }
    return Boolean(result);
  }

  const deleteSolution = async (sol) => {
    let result = await Api.deleteSolution(sol.id);
    if (result){
      refresh();
    }
    return Boolean(result);
  }


  return (
    <Grid container>
      <Grid item xs={6}>
        <Typography variant="subtitle1">
          Existing Solutions  
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Id</TableCell>
                <TableCell>Num Units</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {solutions.map(x => (
                <TableRow key={x.id}>
                  <TableCell>{x.name}</TableCell>
                  <TableCell>{x.id}</TableCell>
                  <TableCell>{x?.dailyAssignments ?? "None"}</TableCell>
                  <TableCell align="right">
                    <Button onClick={() => deleteSolution(x)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={6}>
        <Container>
          <Typography>
            Create New
          </Typography>
          <NewSolutionForm submit={createNewSolution}/>
        </Container>
      </Grid>
    </Grid>
  )

}

const AdminStationView = ({stations, refresh, ...props}) => {

  const createNewStation = async (station) => {
    let result = await Api.postNewStation(station);
    if (result){
      refresh();
    }
  }
  
  return (
    <Grid container justifyContent={"center"}>
    <Grid item xs={6}>
      <Typography variant="subtitle1">
        Existing Stations  
      </Typography>
      <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Longitude</TableCell>
            <TableCell>Latitude</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stations.map(x => 
            <TableRow key={x.id}>
              <TableCell>
                {x.name}
              </TableCell>
              <TableCell>
                {x.longitude}
              </TableCell>
              <TableCell>
                {x.latitude}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </TableContainer>
    </Grid>

    <Grid item xs={6}>
      <Container>
        <Typography>
          Create New
        </Typography>
        <NewStationForm submit={createNewStation}/>
      </Container>
    </Grid>
  </Grid>
  )
}

const AdminPauAssignView = ({assignments, refresh, solutions, stations, ...props}) => {
  
  const createNewAssign = async (assign) => {
    let result = await Api.postNewAssignment(assign);
    if (result){
      refresh();
    }
    return Boolean(result);
  }

  const deleteAssign = async (assign) => {
    let result = await Api.deleteAssign(assign.id);
    if (result){
      refresh();
    }
    return Boolean(result);
  }

  return (
    <Grid container justifyContent="center" spacing={1}>
      <Grid item xs={6}>
        <Typography variant="subtitle1">
          Existing Assignments
        </Typography>
        <TableContainer component={Paper}>
          <TempAssignTable assignments={assignments} deleteAssign={deleteAssign}/>
        </TableContainer>
      </Grid>
      <Grid item xs={6}>
        <NewAssignmentForm 
          solutions={solutions}
          stations={stations}
          submit={createNewAssign}
        />
      </Grid>
    </Grid>
  )
}

const TempAssignTable = ({assignments, deleteAssign, ...props}) => {
  
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Station</TableCell>
          <TableCell>Day Of Week</TableCell>
          <TableCell>StartTime</TableCell>
          <TableCell>Solution Name</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {assignments.map(x =>
          <TableRow key={x.id}>
            <TableCell>{x.station.name}</TableCell>
            <TableCell>{DayOfWeekArray[x.dayOfWeek]}</TableCell>
            <TableCell>{x.startTime}</TableCell>
            <TableCell>{x.parentSolution.name}</TableCell>
            <TableCell>
              <Button onClick={() => deleteAssign(x)}>
                Delete
              </Button>
            </TableCell>
          </TableRow>  
        )}
      </TableBody>
    </Table>
  )
}

const AdminMetricView = ({metrics, refresh, ...props}) => {

  const submit = async (newMetric) => {
    let success = await Api.postNewMetric(newMetric);
    if (success){
      refresh();
    }
  }

  const deleteMetric = async (toDelete) => {
    let success = await Api.deleteMetric(toDelete.id);
    if (success){
      refresh();
    }
  }

  return (
    <Grid container justifyContent={"center"} spacing={1}>
      <Grid item xs={6}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell> Name </TableCell>
              <TableCell> Value </TableCell>
              <TableCell> Solution Id </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {metrics.map(x =>
              <TableRow key={x.id}>
                <TableCell>{x.label}</TableCell>
                <TableCell>{x.value}</TableCell>
                <TableCell>{x.solutionId}</TableCell>
                <TableCell>
                  <Button onClick={() => deleteMetric(x)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Grid>
      <Grid item xs={6}>
        <NewMetricForm submit={submit}/>
      </Grid>
    </Grid>
  )
}

const AdminOptimizationView = ({requests, refresh, ...props}) => {

  const deleteOpt = async (id) => {
    let result = await Api.deleteOptimization(id);
    if (result){
      refresh();
    }
  }

  const deleteFullOpt = async (id) => {
    let result = await Api.deleteFullOptimization(id);
    if (result) {
      refresh();
    }
  }

  return (
    <Grid container justifyContent={"center"}>
      <Grid item xs={8}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Number of Units</TableCell>
              <TableCell>Date Requested</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map(x => 
              <TableRow key={x.id}>
                <TableCell>{x.name}</TableCell>
                <TableCell>{x.status}</TableCell>
                <TableCell>{x.dailyAssignments}</TableCell>
                <TableCell>{x.dateRequested}</TableCell>
                <TableCell>
                  <Button onClick={() => deleteOpt(x.id)}>
                    Delete
                  </Button>
                </TableCell>
                <TableCell>
                  <Button onClick={() => deleteFullOpt(x.id)}>
                    BIG DELETE
                  </Button>
                </TableCell>
              </TableRow>
              )
            }
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  )
}

const AdminOrganizationView = ({organization, refresh, solutions, ...props}) => {
  const [disabled, setDisabled] = useState(false);


  const onChange = async (e) => {
    const newId = e.target.value;

    let success = await Api.postNewCurrentDeployment(newId);
    console.log(success);
    if (success){
      await refresh();
    }
    setDisabled(false);
  }


  return (
    <Grid container justifyContent={"center"}>
      <Grid item xs={6}>
        <Typography>
          Current Deployment: {organization.currentDeployment ? organization.currentDeployment.name : "NONE"}
        </Typography>  
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth>
        <InputLabel id="update-label">Update Current Deploy</InputLabel>
        <Select
          label="Update Current Deploy"
          labelId="update-label"
          autoWidth
          value={organization.currentDeployment ? organization.currentDeployment.id : "none"}
          onChange={(e) => {
            setDisabled(true);
            onChange(e);
          }}
          disabled={disabled}
        >
          <MenuItem value={"none"}>None Selected</MenuItem>
          {
            solutions?.map(x => 
              <MenuItem value={x.id} key={x.id}>{x.name}</MenuItem>  
            )
          }
        </Select>
        </FormControl>
      </Grid>
    </Grid>
  )
}

export const AdminImportExport = ({solutions, ...props}) => {
  const [toExport, setToExport] = useState(null);
  const [exportText, setExportText] = useState(null);
  const [importText, setImportText] = useState("");

  const getExportText = async () => {
    if (toExport === null) { return; }
    let text = await Api.getSolutionExport(toExport.id);
    console.log(text);
    if (text){
      setExportText(text);
    }
  }

  const postImport = async () => {
    if (importText && importText !== ""){
      let result = await Api.postImportSolution(importText);
      if (result){
        setImportText("");
      }else{
        setImportText("Failed!")
      }
    }
  }

  useEffect(() => {
    if (toExport === null){
      setExportText(null);
    }
  }, [toExport])

  return (
    <Grid container justifyContent={"center"} spacing={1}>
      <Grid item container justifyContent={"center"} spacing={1} alignItems="center" xs={6}>
        <Grid item xs={8}>
          <Autocomplete
            fullWidth
            options={solutions}
            getOptionLabel={(sol) => sol.name}
            value={toExport}
            onChange={(e, newValue) => setToExport(newValue)}
            renderInput= {(params) =><TextField {...params} label="Solution"/>}
          />
        </Grid>
        <Grid item xs={4}>
          <Button onClick={getExportText}>Export</Button>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{border: "1px solid lightgrey", borderRadius: "4px", minHeight: "3em", padding:"5px", position: "relative"}}>
            {exportText != null && 
              <IconButton sx={{position: "absolute", bottom: "1%", right:"1%"}}
                onClick={() => navigator.clipboard.writeText(exportText)}
              >
                <ContentCopyIcon/>
              </IconButton>
            }
            <Typography variant="body2">
              {exportText ? exportText : "Export goes here..."}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Grid item container justifyContent={"center"} alignItems="center" xs={6} spacing={1}>
        <Grid item xs={8}>
          <Button fullWidth variant="contained" onClick={postImport}>
            Import
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Import From Json"
            multiline
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const AdminMiscSection = (props) => {
  const [statusText, setStatusText] = useState("Status...");

  const doThing = async () => {
    // let success = await Api.sendTestEmail();
    let success = true;
    let resultText = (success) ? "Success!" : "Fail!";
    setStatusText(resultText);
  }

  return (
    <Grid container justifyContent={"center"} spacing={1}>
      <Grid item xs={4} sx={{textAlign: "center"}}>
        <Button variant="contained" onClick={doThing}>
          Do The Stuff
        </Button>
      </Grid>
      <Grid item xs={10}>
        <Box sx={{border: "1px solid lightgrey", borderRadius: "4px", minHeight: "3em", padding:"5px", position: "relative"}}>
            <Typography variant="body2">
              {statusText}
            </Typography>
        </Box>
        <Typography variant="subtitle1">
          Status
        </Typography>
      </Grid>
    </Grid>
  )
}

const AdminRoleChangeSection = (props) => {
  const roles = UserUtils.RoleObjects;
  const [users, setUsers] = useState([]);
  
  const getRoles = () => {
    let roleArr = [];
    Object.keys(roles).forEach(k => {
      roleArr.push(roles[k]);
    });
    return roleArr;
  }

  const onSubmit = async (user, newRole) => {
    let userId = user.id;
    let newRoleValue = newRole.value;
    let succ = await Api.postUserRoleUpdate(userId, newRoleValue);
    if (succ) {
      let users = await Api.getAllUsers();
      setUsers(users);
    }
  }

  useEffect(() => {
    const func = async () => {
      console.log("HI USERS");
      let users = await Api.getAllUsers();
      setUsers(users);
    }
    func();
  }, []);

  return (
    <Container>
      <UserRoleUpdater users={users} roleObjs={getRoles()} onSubmit={onSubmit}/>
    </Container>
  )
}


export const AdminView = (props) => {
  const {isAuthorized, checkIfUserHasRole} = useUserInfoContext();
  const [openSections, setOpenSections] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [stations, setStations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [optRequests, setOptRequests] = useState([]);
  const [organization, setOrg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useDocumentTitle("Admin");

  useEffect(() => {
    if (isAuthorized && checkIfUserHasRole(UserUtils.RoleObjects['admin'].name)){
      FetchData();
    }
  }, [isAuthorized]);

  // const SubmitKey = async (key) => {
  //   let result = await Api.ALogin(key);
  //   if (result){
  //     setLoggedIn(result);
  //     Api.ApiKey = key;
  //   }
  // }

  const FetchData = async () => {
    // let solPromise = Api.getAllSolutions();
    let statPromise = Api.getAllStations();
    // let assignPromise = Api.getAllAssignments();
    let metricPromise = Api.getAllMetrics();
    let optPromise = Api.getAllOptimizationRequests();
    let userPromise = Api.getAllUsers();

    let opts = await optPromise;
    if (opts != null){
      setOptRequests(opts);
    }

    let stats = await statPromise;
    if (stats != null){
      setStations(stats);
    }

    let metrics = await metricPromise;
    if (metrics != null){
      setMetrics(metrics);
    }

    await getSolutionsAndAssigns();

    await getOrg();
    setLoaded(true);

  }

  const getOrg = async () => {
    let org = await Api.GetOrgWithCurrentDeploy();
    if (org != null){
      setOrg(org);
    }
  }

  const getSolutionsAndAssigns = async () => {
    let solPromise = Api.getAllSolutions();
    let assignPromise = Api.getAllAssignments();
    let sols = await solPromise;
    if (sols != null){
      setSolutions(sols);
    }
    let assigns = await assignPromise;
    if (assigns != null){
      setAssignments(assigns);
    }
  }


  return (
    <Container>
      { checkIfUserHasRole(UserUtils.RoleObjects['admin'].name) ? 
      <>
      { !loaded &&
      <Box sx={{width: "100%", height: "100%"}}>
        <Stack>
          <Skeleton variant="text"/>
          <Skeleton variant="circular"/>
          <Skeleton varaint="rectangular"/>
        </Stack>
      </Box>
      }
      {loaded &&
      <Grid container justifyContent={"center"}>
        <Grid item xs={12}>
          <Typography variant="h4">
            Admin Home
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Solution Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminSolutionView solutions={solutions} refresh={FetchData}/>
            </AccordionDetails>
            </Accordion>
            <Accordion>

            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Station Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminStationView stations={stations} refresh={FetchData}/>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                PAU Assignment Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminPauAssignView assignments={assignments} solutions={solutions} stations={stations} refresh={getSolutionsAndAssigns}/>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Metrics Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminMetricView metrics={metrics} refresh={FetchData}/>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Optimizations Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminOptimizationView requests={optRequests} refresh={FetchData}/>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Organization Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminOrganizationView organization={organization} solutions={solutions.filter(x => x.organizationId === organization?.id)} refresh={getOrg}/>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Import/Export Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminImportExport solutions={solutions} />
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Role Updates
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminRoleChangeSection/>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography sx={{fontWeight: "bold"}}>
                Misc Stuff
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdminMiscSection/>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      }
      </>
    : <UnauthorizedPage home={true}/>}
    </Container>
  )


}